import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { usePreferences } from './preferences-store';
import { apiRequest } from '../lib/api-client';
import { formatDateTime } from '../ui/format';
import { BellIcon } from './nav/icons';
import { isEventReminder, newArrivals, Toasts, type ToastSource } from './Toasts';
import { EventReminder } from '../calendar/EventReminder';
import { useRealtimeReconnect, useRealtimeTopic } from './realtime-store';
import { playNotificationSound } from './sounds';
import { useAuth } from '../auth/auth-store';

/**
 * The bell (context.md sections 133, 206).
 *
 * Pushed, with a poll underneath it (Phase 68). The server publishes `notification.created` on
 * `notifications:<userId>` after the transaction commits, and this invalidates on the frame — so a
 * task assigned to somebody rings in about a second rather than in up to a minute.
 *
 * The slow poll stays, at five minutes rather than one. Not belt and braces for its own sake: a
 * socket can be down for reasons the client cannot see — a proxy that drops idle connections, a
 * laptop that slept through the reconnect — and a bell that is silently an hour stale is worse than
 * one that is a few minutes late. Five minutes is cheap and it is the floor, not the mechanism.
 *
 * The text comes back as a key and its values, never as a sentence: the API answers in English by
 * convention and the interface is pt-BR, so rendering happens here (context.md sections 130, 206).
 */

/**
 * The floor under the socket, not the way this screen normally learns anything.
 *
 * Five minutes: often enough that a dead socket cannot hide for long, rare enough that a thousand
 * tenants sitting on the dashboard are not a load profile.
 */
const POLL_MS = 5 * 60_000;

/** The feed row. `ToastSource` is the part the pop-ups need, so the two cannot drift apart. */
interface NotificationView extends ToastSource {
  created_at: string;
}

interface Feed {
  notifications: NotificationView[];
  unread: number;
}

function render(notification: ToastSource): string {
  const template = t(notification.message_key);

  // An unknown key prints the key, which is a bug somebody should see rather than an empty line.
  return Object.entries(notification.values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    template,
  );
}

export function Notifications(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  /*
   * The meeting alerts on screen right now (Phase 125).
   *
   * Held here rather than derived from the feed, because a dialog somebody dismissed must stay
   * dismissed: the notification is still in the feed until the next poll, and re-deriving would
   * put it straight back on the screen.
   */
  const [reminders, setReminders] = useState<{ id: string; eventId: string }[]>([]);
  const seenReminders = useRef<Set<string> | null>(null);
  const client = useQueryClient();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);

  const { user } = useAuth();

  const feed = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiRequest<Feed>('/v1/notifications?limit=20'),
    refetchInterval: POLL_MS,
    retry: false,
  });

  /*
   * The frame carries an id and a kind, never the text.
   *
   * So this re-reads rather than appending what it was handed: the list comes back through the API,
   * where the record scope and the read state are applied, and a frame cannot put something on
   * somebody's bell that the API would not have given them.
   */
  useRealtimeTopic(user ? `notifications:${user.id}` : null, () => {
    /*
     * A sound on the frame, not on the refetch.
     *
     * The frame is the moment something arrived. Playing it when the list comes back would chime
     * on every poll, every reconnect and every window focus — three sounds for one notification.
     */
    if (preferences.sounds) playNotificationSound();

    void client.invalidateQueries({ queryKey: ['notifications'] });
  });

  // A socket that was down missed whatever happened while it was; the reconnection is the moment to
  // find out, rather than waiting for the poll.
  useRealtimeReconnect(() => {
    void client.invalidateQueries({ queryKey: ['notifications'] });
  });

  const read = useMutation({
    mutationFn: (id: string) => apiRequest(`/v1/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const readAll = useMutation({
    mutationFn: () => apiRequest('/v1/notifications/read-all', { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Closes on a click anywhere else and on Escape, like every other transient panel.
  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent): void => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false);
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = feed.data?.unread ?? 0;
  const rows = feed.data?.notifications;

  /*
   * A meeting about to start raises a dialog rather than a toast.
   *
   * The same "what is new since the last look" rule the toasts use, so opening the product does
   * not throw this morning's alerts at somebody — and so a reminder that arrives while they are
   * looking at something else does interrupt, which is the entire point of it.
   */
  useEffect(() => {
    if (!rows) return;

    const arrived = newArrivals(rows, seenReminders.current);

    seenReminders.current = new Set(rows.map((notification) => notification.id));

    const fresh = rows
      .filter((notification) => arrived.includes(notification.id) && isEventReminder(notification))
      .map((notification) => ({ id: notification.id, eventId: notification.subject_id! }));

    if (fresh.length > 0) setReminders((current) => [...fresh, ...current].slice(0, 3));
  }, [rows]);

  function closeReminder(id: string, markRead: boolean): void {
    setReminders((current) => current.filter((each) => each.id !== id));

    if (markRead) read.mutate(id);
  }

  return (
    <div className="bell" ref={panel}>
      {/* A meeting has a deadline, so its alert is allowed to interrupt and does not fade. */}
      {reminders.length > 0 && (
        <div className="reminders">
          {reminders.map((reminder) => (
            <EventReminder
              key={reminder.id}
              eventId={reminder.eventId}
              onClose={({ markRead }) => closeReminder(reminder.id, markRead)}
            />
          ))}
        </div>
      )}

      {/* The same feed, shown as it arrives rather than only when somebody opens the panel. */}
      {preferences.toasts && (
        <Toasts
          notifications={feed.data?.notifications ?? []}
          loaded={feed.data !== undefined}
          render={render}
        />
      )}

      <button
        type="button"
        className="topbar__action"
        aria-label={t('shell.notifications')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="bell__count" aria-hidden>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label={t('shell.notifications')}>
          <header className="bell__head">
            <span className="strong">{t('shell.notifications')}</span>

            {unread > 0 && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => readAll.mutate()}
              >
                {t('notify.readAll')}
              </button>
            )}
          </header>

          {(feed.data?.notifications.length ?? 0) === 0 ? (
            <p className="muted small bell__empty">{t('notify.empty')}</p>
          ) : (
            <ul className="bell__list">
              {(feed.data?.notifications ?? []).map((notification) => (
                <li key={notification.id} className={notification.read ? '' : 'bell__item--unread'}>
                  <button
                    type="button"
                    className="bell__item"
                    onClick={() => {
                      if (!notification.read) read.mutate(notification.id);
                      if (notification.link) {
                        setOpen(false);
                        navigate(notification.link);
                      }
                    }}
                  >
                    <span>{render(notification)}</span>
                    <span className="muted small">{formatDateTime(notification.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
